import { useEffect, useId, useRef, type ReactNode } from 'react';
import { TriangleAlert } from 'lucide-react';
import { Icon } from './Icon';
import { useT } from '../../i18n';
import { COMMON } from '../../i18n/common';
import './dialog.css';

export interface DialogProps {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
  /**
   * Серьёзное/необратимое действие: кнопка подтверждения — тон «опасно»
   * (`btn--danger`, как `primary.tone: 'danger'` у `FormActions`) со значком
   * предупреждения. Заливка мягкая: насыщенная плоская запрещена на контролах
   * крупнее 32 px.
   */
  danger?: boolean;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
}

/**
 * Примитив подтверждающего диалога — по образцу самодельного в
 * `pages/TemplateDetail.tsx` (публикация версии шаблона), только
 * самодостаточный: фокус-ловушка, Escape и возврат фокуса не требуют от
 * вызывающего экрана держать собственные ref'ы на триггер.
 *
 * Существующие страницы на этот примитив не переведены — у каждой уже есть
 * свой рабочий диалог, переписывать его ради унификации в эту волну не
 * входит.
 */
export function Dialog({ title, children, confirmLabel, onConfirm, onClose, danger = false }: DialogProps) {
  const titleId = useId();
  const t = useT();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  // `onClose` — обычно инлайн-функция со стороны вызывающего экрана, новая
  // на каждый его рендер. Через ref keydown-эффект ниже читает актуальный
  // колбэк, но сам регистрируется один раз на монтирование — а не
  // перевешивается при каждом чужом ре-рендере.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Открытие диалога запоминает, что было в фокусе, и переносит фокус на
  // подтверждение; закрытие — любым путём (Escape, backdrop, кнопки) —
  // возвращает фокус туда, откуда пришли. Один эффект держит оба конца.
  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null;
    confirmRef.current?.focus();
    return () => {
      trigger?.focus?.();
    };
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !cardRef.current) return;
      const nodes = focusableIn(cardRef.current);
      if (nodes.length === 0) return;
      const first = nodes[0]!;
      const last = nodes[nodes.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div
      className="ui-dialog-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="ui-dialog-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={cardRef}
      >
        <h2 id={titleId} className="t-title">
          {title}
        </h2>
        <div className="ui-dialog-card__body t-body">{children}</div>
        <div className="ui-dialog-card__actions">
          <button type="button" className="btn btn--ghost focus-ring" onClick={onClose}>
            {t(COMMON.cancel)}
          </button>
          <button
            type="button"
            className={`btn ${danger ? 'btn--danger' : 'btn--secondary'} focus-ring`}
            ref={confirmRef}
            onClick={onConfirm}
          >
            {danger ? <Icon icon={TriangleAlert} size={18} /> : null}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
